<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div class="d-flex align-items-center">
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ name: 'events' }"
              ><i class="fa-solid fa-arrow-left"></i
              ><span class="ml-25">Back</span></b-button
            >
            <h3 class="ml-1">Event</h3>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="loaded && ['open','closed'].includes((form.status || '').toLowerCase()) && canEditEvent()"
              size="sm"
              variant="outline-success"
              class="mr-50"
              :disabled="sendingInvitations || sendingNewInvitations"
              @click="sendInvitations"
              title="Re-send invitations to all eligible audience members, including previously invited"
            >
              <i class="fa-solid fa-envelope mr-25"></i>
              {{ sendingInvitations ? 'Sending...' : 'Send All Invitations' }}
            </b-button>
            <b-button
              v-if="loaded && ['open','closed'].includes((form.status || '').toLowerCase()) && canEditEvent()"
              size="sm"
              variant="success"
              class="mr-50"
              :disabled="sendingInvitations || sendingNewInvitations"
              @click="sendNewInvitations"
              title="Send invitations only to new audience members who haven't applied or been invited yet"
            >
              <i class="fa-solid fa-paper-plane mr-25"></i>
              {{ sendingNewInvitations ? 'Sending...' : 'Send New Invitations' }}
            </b-button>
            <b-button
              v-if="canEditEvent()"
              size="sm"
              variant="primary"
              :to="{ name: 'event-edit', params: { id: $route.params.id } }"
              >Edit</b-button
            >
          </div>
        </div>

        <div v-if="loaded">
          <b-tabs class="event-tabs">
            <b-tab title="General">
              <b-card title="General">
                <b-row>
                  <b-col md="6"
                    ><div class="mb-25">
                      <strong>Course:</strong>
                      {{ (selectedCourse && selectedCourse.title) || "-" }}
                    </div></b-col
                  >
                  <b-col md="3"
                    ><div class="mb-25">
                      <strong>Year:</strong> {{ form.year }}
                    </div></b-col
                  >
                  <b-col md="3"
                    ><div class="mb-25">
                      <strong>Status:</strong> {{ form.status }}
                      <b-badge
                        v-if="form && form.is_eft"
                        variant="light-info"
                        class="ml-25"
                      >
                        Educational Fee Training
                      </b-badge>
                    </div></b-col
                  >
                </b-row>
                <b-row>
                  <b-col md="6"
                    ><div class="mb-25">
                      <strong>Time zone:</strong> {{ form.time_zone }}
                    </div></b-col
                  >
                  <b-col md="6"
                    ><div class="mb-25">
                      <strong>Application method:</strong>
                      {{ form.application_method || "-" }}
                    </div></b-col
                  >
                </b-row>
                <b-row>
                  <b-col md="6"
                    ><div class="mb-25">
                      <strong>Application deadline:</strong>
                      {{
                        form.application_deadline
                          ? dateFormat(form.application_deadline)
                          : "-"
                      }}
                    </div></b-col
                  >
                  <b-col md="6"
                    ><div class="mb-25">
                      <strong>Max participants:</strong>
                      {{ form.max_participants || "-" }}
                    </div></b-col
                  >
                </b-row>
                <div class="mb-25">
                  <strong>Title:</strong>
                  {{
                    form.title_override ||
                      (selectedCourse && selectedCourse.title) ||
                      "-"
                  }}
                </div>
                <div class="mb-25">
                  <strong>Description:</strong>
                  <div class="text-pre">
                    {{ form.description_override || "-" }}
                  </div>
                </div>
                <div class="mb-25">
                  <strong>Admin notes:</strong>
                  <div class="text-pre">{{ form.notes_admin || "-" }}</div>
                </div>

                <hr />
                <h5>Parts</h5>
                <div
                  v-for="(s, idx) in sessions"
                  :key="s.id ? s.id : idx"
                  class="session-card border rounded p-1 mb-1"
                >
                  <div
                    class="d-flex justify-content-between align-items-center session-header"
                  >
                    <div class="d-flex align-items-center">
                      <h6 class="mb-0">{{ sessionTitle(idx) }}</h6>
                      <span
                        class="text-muted small ml-50"
                        v-if="s.start_at || s.end_at"
                      >
                        {{ s.start_at ? dateFormat(s.start_at) : "-" }}
                        <span class="mx-25">→</span>
                        {{ s.end_at ? dateFormat(s.end_at) : "-" }}
                      </span>
                    </div>
                    <div class="d-flex align-items-center">
                      <b-badge variant="light-primary" class="mr-50">{{
                        sessionVenueName(s)
                      }}</b-badge>
                      <b-button
                        v-if="$CanEvent('event_edit', form || [])"
                        size="sm"
                        variant="outline-primary"
                        :to="{
                          name: 'event-attendance',
                          params: { sessionId: s.id },
                        }"
                      >
                        Attendance
                      </b-button>
                    </div>
                  </div>

                  <b-row class="mt-50">
                    <b-col md="6" class="mb-50">
                      <label class="text-muted small mb-25">Trainer</label>
                      <div class="font-weight-600">
                        {{ sessionTrainerName(s) }}
                      </div>
                    </b-col>
                    <b-col md="6" class="mb-50">
                      <label class="text-muted small mb-25">Passcode</label>
                      <div>{{ s.meeting_passcode || "-" }}</div>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="12">
                      <label class="text-muted small mb-25">Meeting URL</label>
                      <div>
                        <a
                          v-if="s.meeting_url"
                          :href="normalizeUrl(s.meeting_url)"
                          target="_blank"
                          rel="noopener"
                          >{{ s.meeting_url }}</a
                        >
                        <span v-else>-</span>
                      </div>
                    </b-col>
                  </b-row>

          <b-row class="mt-50" v-if="sortedSessionAddons(s).length">
            <b-col cols="12">
              <label class="text-muted small mb-25">Add-ons</label>
              <div>
                <div
                  v-for="a in sortedSessionAddons(s)"
                  :key="a.id || a.code || a.label"
                  class="border rounded p-50 mb-25 bg-white"
                >
                  <div class="d-flex align-items-start justify-content-between">
                    <div>
                      <div class="font-weight-600">
                        {{ addonTitle(a) }}
                        <span class="text-muted" v-if="a.code">({{ a.code }})</span>
                        <b-badge
                          v-if="a.is_required"
                          variant="light-primary"
                          class="ml-25"
                        >
                          Required
                        </b-badge>
                      </div>
                      <div class="text-muted small" v-if="a.starts_at || a.ends_at">
                        {{ addonRange(a) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
                </div>
              </b-card>
            </b-tab>

            <b-tab title="Applications" v-if="$CanEvent('edit_event',form || {})">
              <b-card title="Applications">
                <event-applications
                  :event-id="$route.params.id"
                  :embedded="true"
                />
              </b-card>
            </b-tab>

            <b-tab title="Materials">
              <b-card title="Materials">
                <b-table
                  small
                  responsive
                  bordered
                  :items="visibleMaterials"
                  :fields="materialsFields"
                  show-empty
                  empty-text="No materials"
                >
                  <template #cell(title)="{ item }">{{ item.title }}</template>
                  <template #cell(link)="{ item }">
                    <div v-if="item.external_url">
                      <a :href="item.external_url" target="_blank">External</a>
                    </div>
                    <div v-else-if="item.file_path">
                      <a :href="materialFileHref(item.file_path)" target="_blank">Download</a>
                    </div>
                    <div v-else>-</div>
                  </template>
                  <template #cell(visible)="{ item }"
                    >{{ item.visible_from ? dateFormat(item.visible_from) : "-" }}
                    to
                    {{ item.visible_to ? dateFormat(item.visible_to) : "-" }}</template
                  >
                  <template #cell(accepted)="{ item }"
                    ><b-badge
                      :variant="
                        item.is_visible_to_accepted_only
                          ? 'light-info'
                          : 'light-secondary'
                      "
                      >{{
                        item.is_visible_to_accepted_only ? "Yes" : "No"
                      }}</b-badge
                    ></template
                  >
                  <template #cell(course)="{ item }">{{
                    (item.course && item.course.title) || "-"
                  }}</template>
                  <template #cell(actions)="{ item }">
                    <div v-if="canEditEvent()">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        class="mr-25"
                        @click="goEditMaterial(item)"
                        >Edit</b-button
                      >
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="deleteMaterial(item)"
                        >Delete</b-button
                      >
                    </div>
                    <div v-else>-</div>
                  </template>
                </b-table>
                <div v-if="canEditEvent()">
                  <b-button
                    size="sm"
                    variant="primary"
                    :to="{ name: 'event-edit', params: { id: $route.params.id } }"
                    >Manage Materials</b-button
                  >
                </div>
              </b-card>
            </b-tab>

            <b-tab title="Event Reminders" v-if="$CanEvent('edit_event',form || {})">
              <b-card title="Event Reminders">
                <div
                  v-if="
                    form &&
                      form.event_reminders &&
                      form.event_reminders.length > 0
                  "
                >
                  <div
                    v-for="(reminder, idx) in (form && form.event_reminders) || []"
                    :key="reminder.id"
                    class="border rounded p-1 mb-1"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-0">
                          <i class="fa-solid fa-bell mr-25"></i>
                          {{ reminder.days_before }} day{{
                            reminder.days_before > 1 ? "s" : ""
                          }}
                          before
                        </h6>
                        <p v-if="reminder.message" class="text-muted small mb-0 mt-25">
                          {{ reminder.message }}
                        </p>
                      </div>
                      <b-badge
                        :variant="
                          reminder.is_active ? 'light-success' : 'light-secondary'
                        "
                      >
                        {{ reminder.is_active ? "Active" : "Inactive" }}
                      </b-badge>
                    </div>
                  </div>
                </div>
                <p v-else class="text-muted mb-0">
                  No reminders set for this event.
                </p>
              </b-card>
            </b-tab>

            <b-tab title="Application Invitations" v-if="$CanEvent('edit_event',form || {})" @click="loadInvitesOnce">
              <b-card title="Application Invitations">
                <b-overlay :show="invitesLoading" rounded="sm" no-fade>
                  <b-table
                    small
                    bordered
                    responsive
                    :items="invites"
                    :fields="invitesFields"
                    show-empty
                    empty-text="No invitations sent"
                  >
                    <template #cell(user)="{ item }">
                      <div v-if="item.user">
                        <p class="mb-0 font-weight-600">{{ item.user.name }} {{ item.user.surname || '' }}</p>
                        <small class="text-muted">{{ item.user.email }}</small>
                      </div>
                      <span v-else>-</span>
                    </template>
                    <template #cell(expires_at)="{ item }">
                      {{ item.expires_at ? dateFormat(item.expires_at) : '-' }}
                    </template>
                    <template #cell(used_at)="{ item }">
                      <span v-if="item.used_at">
                        <b-badge variant="light-success">Used</b-badge>
                        <small class="ml-25">{{ dateFormat(item.used_at) }}</small>
                      </span>
                      <b-badge v-else variant="light-secondary">Not used</b-badge>
                    </template>
                    <template #cell(created_at)="{ item }">
                      {{ item.created_at ? dateFormat(item.created_at) : '-' }}
                    </template>
                  </b-table>
                </b-overlay>
              </b-card>
            </b-tab>
          </b-tabs>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>
<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BTable,
  BBadge,
  BButton,
  BTabs,
  BTab,
} from "bootstrap-vue";
import store from "@/store";
import eventsStoreModule from "./eventsStoreModule";
import { onUnmounted } from "@vue/composition-api";
import EventApplications from "./EventApplications.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BTable,
    BBadge,
    BButton,
    BTabs,
    BTab,
    EventApplications,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      sendingInvitations: false,
      sendingNewInvitations: false,
      form: {},
      selectedCourse: null,
      sessions: [],
      materials: [],
      myApplicationStatus: null,
      materialsFields: [
        { key: "title", label: "Title" },
        { key: "link", label: "Link" },
        { key: "visible", label: "Visible" },
        { key: "accepted", label: "Accepted only" },
        { key: "course", label: "Course" },
        { key: "actions", label: "Actions" },
      ],
      invites: [],
      invitesLoading: false,
      invitesLoaded: false,
      invitesFields: [
        { key: "id", label: "ID" },
        { key: "user", label: "User" },
        { key: "expires_at", label: "Expires" },
        { key: "used_at", label: "Status" },
        { key: "created_at", label: "Sent At" },
      ],
    };
  },
  computed: {
    materialsURL() {
      return this.$store.state.app.materialsURL;
    },
    isMyApplicationAccepted() {
      return String(this.myApplicationStatus || "").toLowerCase() === "accepted";
    },
    visibleMaterials() {
      const canBypass = this.canEditEvent();
      const accepted = this.isMyApplicationAccepted;
      return (this.materials || []).filter((m) => {
        const acceptedOnly = !!(
          m && (m.is_visible_to_accepted_only || m.accepted_only)
        );
        if (!acceptedOnly) return true;
        return accepted || canBypass;
      });
    },
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
  },
  created() {
    this.load();
  },
  methods: {
    async sendInvitations() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        'Send invitation emails to all eligible audience members for this event?',
        {
          title: 'Send Invitations',
          okVariant: 'success',
          okTitle: 'Send',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        }
      );
      if (!confirm) return;
      try {
        this.sendingInvitations = true;
        await store.dispatch('events/sendInvitations', { id: this.$route.params.id });
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Invitations queued', icon: 'CheckIcon', variant: 'success' },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Error', text: e?.response?.data?.message || 'Failed to send invitations', icon: 'AlertTriangleIcon', variant: 'danger' },
        });
      } finally {
        this.sendingInvitations = false;
      }
    },
    async sendNewInvitations() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        'Send invitation emails only to new audience members who have not yet applied or been invited?',
        {
          title: 'Send New Invitations',
          okVariant: 'success',
          okTitle: 'Send',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        }
      );
      if (!confirm) return;
      try {
        this.sendingNewInvitations = true;
        await store.dispatch('events/sendNewInvitations', { id: this.$route.params.id });
        this.$toast({
          component: ToastificationContent,
          props: { title: 'New invitations queued', icon: 'CheckIcon', variant: 'success' },
        });
        // Refresh invites tab if already loaded
        if (this.invitesLoaded) {
          this.invitesLoaded = false;
          this.loadInvitesOnce();
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Error', text: e?.response?.data?.message || 'Failed to send new invitations', icon: 'AlertTriangleIcon', variant: 'danger' },
        });
      } finally {
        this.sendingNewInvitations = false;
      }
    },
    canManageAttendance() {
      // Event admins can manage attendance regardless of base permission
      return this.$CanEvent('manage_event_attendance', this.form.admins || this.form || []);
    },
    dateFormat(val) {
      if (!val) return "-";
      // Strip timezone info so the stored datetime is treated as local time, not UTC
      const local = String(val).slice(0, 19).replace("T", " ");
      const [datePart, timePart] = local.split(" ");
      const [y, mo, d] = datePart.split("-").map(Number);
      const [h, mi, s] = (timePart || "00:00:00").split(":").map(Number);
      const date = new Date(y, mo - 1, d, h, mi, s || 0);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return `${day}-${month}-${year} ${time}`;
    },
    async load() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const res = await store.dispatch("events/getEventById", { id });
        const ev = res.data;
        this.form = ev;
        this.selectedCourse = ev.course;
        const sessionsRes = await store.dispatch("events/listSessions", { id });
        this.sessions = sessionsRes.data;
        const mats = await store.dispatch("events/listMaterials", { id });
        this.materials = mats.data || [];

        // Used to decide whether to show accepted-only materials to the current user.
        // If fetching user applications fails, we default to hiding accepted-only materials.
        try {
          const appsRes = await store.dispatch("events/getUserApplications");
          const mine = (appsRes.data || []).find(
            (a) => String(a.event_id) === String(id)
          );
          this.myApplicationStatus = (mine && mine.status) || null;
        } catch (e) {
          this.myApplicationStatus = null;
        }

        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
    sessionVenueName(s) {
      // API'den venue object olarak geliyor
      return (s.venue && s.venue.name) || "-";
    },
    sessionTrainerName(s) {
      // API'den primary_trainer object olarak geliyor (snake_case)
      return (s.primary_trainer && s.primary_trainer.name) || "-";
    },
    goEditMaterial() {
      this.$router.push({
        name: "event-edit",
        params: { id: this.$route.params.id },
      });
    },
    canEditEvent() {
      return this.$CanEvent('edit_event', this.form || {});
    },
    async deleteMaterial(item) {
      if (!this.$CanEvent('manage_event_materials', this.form.admins || this.form || [])) return;
      const ok = await this.$bvModal.msgBoxConfirm("Delete this material?", {
        size: "sm",
        title: "Confirm",
        okVariant: "primary",
        okTitle: "Yes",
        cancelTitle: "No",
        cancelVariant: "outline-secondary",
        centered: true,
      });
      if (!ok) return;
      await store.dispatch("events/deleteMaterial", { materialId: item.id });
      this.materials = this.materials.filter((m) => m.id !== item.id);
    },

    sessionTitle(idx) {
      const title =
        this.form.title_override ||
        (this.selectedCourse ? this.selectedCourse.title : "");
      return title ? `${title} - Part ${idx + 1}` : `Part ${idx + 1}`;
    },
    materialFileHref(path) {
      const base = this.materialsURL || '';
      return base.replace(/\/$/, '') + '/' + String(path || '').replace(/^\//, '');
    },
    sortedSessionAddons(session) {
      const list = (session && Array.isArray(session.addons)) ? session.addons : [];
      return list.slice().sort((a, b) => {
        const ao = (a && (a.sort_order ?? a.sort)) ?? 0;
        const bo = (b && (b.sort_order ?? b.sort)) ?? 0;
        if (ao !== bo) return ao - bo;
        return String((a && (a.label || a.code)) || '').localeCompare(String((b && (b.label || b.code)) || ''));
      });
    },
    addonTitle(a) {
      return (a && (a.label || a.code)) || '-';
    },
    normalizeUrl(url) {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      return 'https://' + url;
    },
    addonRange(a) {
      const st = a && a.starts_at ? this.dateFormat(a.starts_at) : '-';
      const en = a && a.ends_at ? this.dateFormat(a.ends_at) : '-';
      if (st === '-' && en === '-') return '';
      return `${st} → ${en}`;
    },
    async loadInvitesOnce() {
      if (this.invitesLoaded) return;
      try {
        this.invitesLoading = true;
        const id = this.$route.params.id;
        const res = await store.dispatch('events/listApplicationInvites', { id });
        this.invites = res.data || [];
        this.invitesLoaded = true;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load invitations',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.invitesLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.event-tabs ::v-deep .nav-tabs .nav-link {
  font-weight: 700;
  font-size: 1.15rem;
}

.event-tabs ::v-deep .nav-tabs .nav-link.active {
  font-weight: 700;
}

.text-pre {
  white-space: pre-wrap;
}
.session-card {
  background: #fafbfc;
}
.session-header {
  padding-bottom: 0.25rem;
  border-bottom: 1px dashed #e9ecef;
}
</style>
